/**
 * Custom hook for managing swimlane visibility and event organization
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import {  buildSwimLanesFromEvents,  organizeEventsByLane, type TimelineEvent } from './timeline-utils';

interface UseTimelineSwimLanesParams {
    allTimelineEvents: TimelineEvent[];
    defaultVisibleLanes?: Set<string>;
}

interface UseTimelineSwimLanesReturn {
    visibleSwimLanes: Set<string>;
    handleSwimLaneToggle: (laneId: string) => void;
    isLaneVisible: (laneId: string) => boolean;
    swimLanes: ReturnType<typeof buildSwimLanesFromEvents>;
    eventsByLane: Map<string, TimelineEvent[][]>;
}

/**
 * Manages swimlane visibility state and event organization
 */
export function useTimelineSwimLanes({
    allTimelineEvents,
    defaultVisibleLanes = new Set([
        'biblical-composition',
        'biblical-narrative',
        'israel-ruler',
        'israel-prophet',
        'empires-peoples',
        'political',
        'military',
        'culture-society'
    ])
}: UseTimelineSwimLanesParams): UseTimelineSwimLanesReturn {
    // Build swim lanes from events first
    const swimLanes = useMemo(() => {
        return buildSwimLanesFromEvents(allTimelineEvents);
    }, [allTimelineEvents]);

    // Initialize visible lanes to include all lanes (parent + subcategories)
    const initialVisibleLanes = useMemo(() => {
        const allLaneIds = new Set(defaultVisibleLanes);
        // Add all subcategory lanes that exist in the data
        swimLanes.forEach(lane => {
            if (lane.isSubcategory && lane.parentLaneId && defaultVisibleLanes.has(lane.parentLaneId)) {
                allLaneIds.add(lane.id);
            }
        });
        return allLaneIds;
    }, [swimLanes, defaultVisibleLanes]);

    // State - Swimlane Toggles
    const [visibleSwimLanes, setVisibleSwimLanes] = useState<Set<string>>(initialVisibleLanes);

    // Effect: Automatically add new lanes when swimLanes changes (e.g., "show more events")
    useEffect(() => {
        setVisibleSwimLanes(prev => {
            const updated = new Set(prev);
            let hasChanges = false;
            
            // Add any new lanes that should be visible by default
            swimLanes.forEach(lane => {
                if (!updated.has(lane.id)) {
                    // Add parent lanes if they're in defaultVisibleLanes
                    if (!lane.isSubcategory && defaultVisibleLanes.has(lane.id)) {
                        updated.add(lane.id);
                        hasChanges = true;
                    }
                    // Add subcategory lanes if their parent is visible
                    else if (lane.isSubcategory && lane.parentLaneId && defaultVisibleLanes.has(lane.parentLaneId)) {
                        updated.add(lane.id);
                        hasChanges = true;
                    }
                }
            });
            
            return hasChanges ? updated : prev;
        });
    }, [swimLanes, defaultVisibleLanes]);

    // Handler for swimlane toggle
    const handleSwimLaneToggle = useCallback((laneId: string) => {
        setVisibleSwimLanes(prev => {
            const next = new Set(prev);
            if (next.has(laneId)) {
                next.delete(laneId);
            } else {
                next.add(laneId);
            }
            return next;
        });
    }, []);

    // Helper to check if a lane is visible
    // Each lane (including subcategories) has independent visibility
    const isLaneVisible = useCallback((laneId: string): boolean => {
        return visibleSwimLanes.has(laneId);
    }, [visibleSwimLanes]);

    // Organize events by lane
    const eventsByLane = useMemo(() => {
        return organizeEventsByLane(allTimelineEvents);
    }, [allTimelineEvents]);

    return {
        visibleSwimLanes,
        handleSwimLaneToggle,
        isLaneVisible,
        swimLanes,
        eventsByLane
    };
}

